import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Activity, 
  Database, 
  Shield, 
  Settings,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Download,
  RefreshCw,
  Trash2,
  User,
  Globe,
  Server,
  HardDrive,
  Cpu,
  Memory
} from 'lucide-react';

const AdminCenter = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    activeSessions: 0,
    totalUsers: 0,
    systemHealth: 'healthy',
    dbConnections: 0
  });
  const [loading, setLoading] = useState(false);

  // Simulation de données en temps réel
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        // Ici tu peux ajouter tes vraies API calls
        const response = await fetch('/api/admin/stats');
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh toutes les 30s
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'sessions', label: 'Sessions actives', icon: Users },
    { id: 'users', label: 'Gestion utilisateurs', icon: User },
    { id: 'system', label: 'Système', icon: Server },
    { id: 'database', label: 'Base de données', icon: Database },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'settings', label: 'Paramètres', icon: Settings }
  ];

  const handleAction = (action, message) => {
    if (window.confirm(message)) {
      setLoading(true);
      // Simulation d'action
      setTimeout(() => {
        setLoading(false);
        alert(`Action "${action}" exécutée avec succès`);
      }, 2000);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Sessions actives</p>
              <p className="text-2xl font-bold text-blue-900">{stats.activeSessions}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-sm font-medium">Utilisateurs totaux</p>
              <p className="text-2xl font-bold text-green-900">{stats.totalUsers}</p>
            </div>
            <User className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-600 text-sm font-medium">Santé système</p>
              <p className="text-2xl font-bold text-yellow-900 capitalize">{stats.systemHealth}</p>
            </div>
            <Activity className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Connexions BDD</p>
              <p className="text-2xl font-bold text-purple-900">{stats.dbConnections}</p>
            </div>
            <Database className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-600" />
            Activité système
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">CPU</span>
              <span className="text-sm font-medium">45%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Mémoire</span>
              <span className="text-sm font-medium">62%</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm text-gray-600">Disque</span>
              <span className="text-sm font-medium">38%</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
            Alertes récentes
          </h3>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-orange-50 rounded border border-orange-200">
              <AlertTriangle className="h-4 w-4 text-orange-600 mr-2" />
              <span className="text-sm text-orange-800">Session expirée détectée</span>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded border border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-sm text-green-800">Sauvegarde automatique réussie</span>
            </div>
            <div className="flex items-center p-3 bg-blue-50 rounded border border-blue-200">
              <Clock className="h-4 w-4 text-blue-600 mr-2" />
              <span className="text-sm text-blue-800">Nettoyage planifié dans 2h</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SessionsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Sessions actives</h3>
        <button 
          onClick={() => handleAction('refresh-sessions', 'Actualiser la liste des sessions ?')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Début</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière activité</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">admin</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">192.168.1.100</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">09:30</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">il y a 2 min</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <button 
                  onClick={() => handleAction('terminate-session', 'Terminer cette session ?')}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const SystemTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Cpu className="h-5 w-5 mr-2 text-blue-600" />
            Performance
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>CPU</span>
                <span>45%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{width: '45%'}}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>RAM</span>
                <span>62%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-yellow-600 h-2 rounded-full" style={{width: '62%'}}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Disque</span>
                <span>38%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{width: '38%'}}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <HardDrive className="h-5 w-5 mr-2 text-green-600" />
            Actions système
          </h3>
          <div className="space-y-3">
            <button 
              onClick={() => handleAction('clear-cache', 'Vider le cache système ?')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Vider le cache
            </button>
            <button 
              onClick={() => handleAction('backup-db', 'Lancer une sauvegarde de la base de données ?')}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Sauvegarde BDD
            </button>
            <button 
              onClick={() => handleAction('cleanup-sessions', 'Nettoyer les sessions expirées ?')}
              className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
            >
              Nettoyer sessions
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const DatabaseTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2 text-purple-600" />
          Statistiques base de données
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-900">847</p>
            <p className="text-sm text-purple-600">Sessions totales</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-900">12</p>
            <p className="text-sm text-blue-600">Connexions actives</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-900">99.8%</p>
            <p className="text-sm text-green-600">Uptime</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Actions base de données</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button 
            onClick={() => handleAction('optimize-db', 'Optimiser la base de données ? Cette opération peut prendre quelques minutes.')}
            className="bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            <Download className="h-4 w-4" />
            Optimiser BDD
          </button>
          <button 
            onClick={() => handleAction('export-backup', 'Exporter une sauvegarde de la base de données ?')}
            className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exporter sauvegarde
          </button>
        </div>
      </div>
    </div>
  );

  const SecurityTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-red-600" />
          Sécurité et surveillance
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-green-800">Pare-feu actif</span>
            </div>
            <span className="text-green-600 text-sm font-medium">Actif</span>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
              <span className="text-green-800">SSL/TLS</span>
            </div>
            <span className="text-green-600 text-sm font-medium">Valide</span>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
              <span className="text-yellow-800">Tentatives de connexion échouées</span>
            </div>
            <span className="text-yellow-600 text-sm font-medium">3 dernières 24h</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Journaux de sécurité</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          <div className="text-sm p-3 bg-gray-50 rounded">
            <span className="text-gray-500">2025-01-14 10:30:15</span> - Connexion réussie: admin
          </div>
          <div className="text-sm p-3 bg-red-50 rounded">
            <span className="text-gray-500">2025-01-14 09:45:22</span> - Tentative de connexion échouée: 192.168.1.50
          </div>
          <div className="text-sm p-3 bg-gray-50 rounded">
            <span className="text-gray-500">2025-01-14 09:12:08</span> - Session expirée: user123
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab />;
      case 'sessions':
        return <SessionsTab />;
      case 'system':
        return <SystemTab />;
      case 'database':
        return <DatabaseTab />;
      case 'security':
        return <SecurityTab />;
      default:
        return <OverviewTab />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Centre d'Administration</h1>
          <p className="text-gray-600">Monitoring et gestion du système</p>
        </div>

        {/* Navigation tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
              <span>Chargement...</span>
            </div>
          </div>
        )}

        {/* Tab content */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminCenter;
